'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Lightweight route-change progress bar. Kept intentionally simple:
// one track + one bar, driven by a single rAF loop, styled entirely from
// globals.css (.nav-progress-track / .nav-progress-bar) so theme colors
// (var(--border)) and prefers-reduced-motion both apply correctly — an
// earlier version hardcoded a white track and injected its own <style>
// tag with sparks/glow layers, which broke on light mode and did a lot of
// work for a route indicator.
export default function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const prevRouteRef = useRef(`${pathname}${searchParams}`);
  const startTimeRef = useRef(0);

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
  };

  // Fast start, easing toward 90% — never claims to finish until the new
  // route actually lands, then snaps to 100% and fades out.
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

  useEffect(() => {
    const currentRoute = `${pathname}${searchParams}`;
    if (prevRouteRef.current === currentRoute) return;
    prevRouteRef.current = currentRoute;

    clear();
    setProgress(0);
    setVisible(true);

    startTimeRef.current = performance.now();
    const run = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const t = Math.min(elapsed / 500, 1);
      setProgress(easeOut(t) * 90);
      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(run);
      } else {
        timerRef.current = setTimeout(() => {
          setProgress(100);
          timerRef.current = setTimeout(() => {
            setVisible(false);
            timerRef.current = setTimeout(() => setProgress(0), 50);
          }, 200);
        }, 80);
      }
    };
    animFrameRef.current = requestAnimationFrame(run);

    return clear;
  }, [pathname, searchParams]);

  if (!visible && progress === 0) return null;

  return (
    <div className="nav-progress-track" aria-hidden="true">
      <div
        className={`nav-progress-bar${visible && progress < 98 ? ' is-loading' : ''}`}
        style={{ width: `${progress}%`, opacity: visible ? 1 : 0 }}
      />
    </div>
  );
}
